import React, { useState } from 'react';
import { graphql } from 'gatsby';

import { propTypes } from 'src/modules/pages';
import { prop } from 'src/utils';
import { Title, SEO, Grid, Content, Image } from '../atoms';
import { Page } from '../atoms/Page';
import { Footer, Menu } from '../organisms';
import { IconCard, ImageCard, HeroImage, PrivacyPopUp } from '../molecules';

const getPublicURL = prop('publicURL');

export function SectionsPageTemplate({ sections }) {
  return (
    <div className="flex flex-col items-center w-full">
      {(sections || []).map((section, sectionIndex) => {
        switch (section.type) {
          case 'image':
            // eslint-disable-next-line react/no-array-index-key
            return <HeroImage key={`${section.type}-${sectionIndex}`} className="mt-16" image={section.image} />;
          case 'title':
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Title key={`${section.type}-${sectionIndex}`} className="mt-16 -mb-8">
                {section.title}
              </Title>
            );
          case 'subtitle':
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Title.Sm key={`${section.type}-${sectionIndex}`} className="mt-16 -mb-8">
                {section.subtitle}
              </Title.Sm>
            );
          case 'content':
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Content key={`${section.type}-${sectionIndex}`} className="w-full mt-16 sm:px-20 xl:px-40">
                {section.content}
              </Content>
            );
          case 'contentImageRight':
            return (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`${section.type}-${sectionIndex}`}
                className="w-full mt-16 grid grid-cols-1 lg:grid-cols-3 row-gap-20 lg:col-gap-20"
              >
                <div className="flex flex-col items-stretch justify-center col-span-2">
                  <Content className="sm:px-20 lg:px-0">{section.content}</Content>
                </div>
                <div className="flex flex-col items-stretch justify-center col-span-1">
                  <div className={`bg-${section.imageRightColor}-200`}>
                    <Image
                      data={section.imageRight || 'no-image'}
                      className="object-cover w-full shadow-2xl h-80 transform rotate-2 lg:rotate-5"
                    />
                  </div>
                </div>
              </div>
            );
          case 'contentImageLeft':
            return (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`${section.type}-${sectionIndex}`}
                className="w-full mt-16 grid grid-cols-1 lg:grid-cols-3 row-gap-10 lg:col-gap-20"
              >
                <div className="flex flex-col items-stretch justify-center col-span-1">
                  <div className={`bg-${section.imageLeftColor}-200 `}>
                    <Image
                      data={section.imageLeft || 'no-image'}
                      className="object-cover w-full shadow-2xl h-80 transform -rotate-2 lg:-rotate-5"
                    />
                  </div>
                </div>
                <div className="flex flex-col items-stretch justify-center col-span-2">
                  <Content className="sm:px-20 lg:px-0">{section.content}</Content>
                </div>
              </div>
            );
          case 'videoUrl': {
            const url = section.videoUrl || '';
            const isYoutube = url.includes('youtube');

            if (!url) return null;

            if (isYoutube) {
              return (
                <div className="flex justify-center w-full mt-16 sm:px-20 xl:px-40">
                  <iframe className="w-full h-88 sm:w-128 sm:h-104" src={section.videoUrl} title="video" />
                </div>
              );
            }

            return (
              <div className="flex justify-center w-full mt-16 sm:px-20 xl:px-40">
                <video className="w-full h-88 sm:w-128 sm:h-104" controls>
                  <source src={url} type="video/mp4" />
                </video>
              </div>
            );
          }
          case 'videoFile': {
            const url = getPublicURL(section.videoFile);
            if (!url) return null;

            return (
              <div className="flex justify-center w-full mt-16 sm:px-20 xl:px-40">
                <video className="w-full h-88 sm:w-128 sm:h-104" controls>
                  <source src={url} type="video/mp4" />
                </video>
              </div>
            );
          }
          case 'grid': {
            return (
              <Grid
                // eslint-disable-next-line react/no-array-index-key
                key={`${section.type}-${sectionIndex}`}
                maxColumns={section.maxCardsPerRow}
                className="w-full mt-16"
              >
                {(section.cards || []).map((card, cardIndex) => {
                  switch (card.type) {
                    case 'iconCard':
                    case 'iconLinkCard':
                    case 'iconLinkButtonCard':
                    case 'iconFileCard':
                    case 'iconFileButtonCard':
                      return (
                        <div className="flex items-center justify-center">
                          <IconCard
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${card.type}-${cardIndex}`}
                            url={card.url}
                            file={card.file}
                            external={card.external}
                            button={card.button}
                            icon={card.icon}
                            title={card.title}
                            content={card.content}
                          />
                        </div>
                      );
                    case 'imageCard':
                    case 'imageLinkCard':
                    case 'imageLinkButtonCard':
                    case 'imageFileCard':
                    case 'imageFileButtonCard':
                      return (
                        <div className="flex items-center justify-center">
                          <ImageCard
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${card.type}-${cardIndex}`}
                            url={card.url}
                            file={card.file}
                            external={card.external}
                            button={card.button}
                            image={card.image}
                            objectFit={card.objectFit}
                            title={card.title}
                            content={card.content}
                          />
                        </div>
                      );
                    default:
                      return null;
                  }
                })}
              </Grid>
            );
          }
          default:
            return null;
        }
      })}
    </div>
  );
}

SectionsPageTemplate.propTypes = {
  sections: propTypes.sections.isRequired,
};

export default function SectionsPage({ data, pageContext }) {
  const { menus = [], slug, metadata } = pageContext;
  const { privacyPolicyPopupContent, privacyPolicyPopupButton, homeMenuTitle, footer } = data.language.frontmatter;
  const { lang, metaTitle, metaDescription, sections } = data.page.frontmatter;
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <Page>
      <SEO
        lang={lang}
        title={metaTitle}
        description={metaDescription}
        metadata={metadata}
        bodyClassName={isMenuOpen ? 'overflow-hidden' : ''}
      />
      <div className="container px-4 md:p-6">
        <Menu
          lang={lang}
          homeMenuTitle={homeMenuTitle}
          menus={menus}
          current={slug}
          onOpen={() => setIsMenuOpen(true)}
          onClose={() => setIsMenuOpen(false)}
        />
        <SectionsPageTemplate
          metadata={metadata}
          metaTitle={metaTitle}
          metaDescription={metaDescription}
          lang={lang}
          slug={slug}
          menus={menus}
          homeMenuTitle={homeMenuTitle}
          sections={sections}
          footer={footer}
        />
        <Footer
          lang={lang}
          contactUs={footer.contactUs}
          usefulLinks={footer.usefulLinks}
          ourTeam={footer.ourTeam}
          footerImage1={footer.footerImage1}
          footerImage2={footer.footerImage2}
          copyright={footer.copyright}
        />
      </div>
      <PrivacyPopUp content={privacyPolicyPopupContent} button={privacyPolicyPopupButton} />
    </Page>
  );
}

SectionsPage.propTypes = {
  pageContext: propTypes.pageContext().isRequired,
  data: propTypes.data().isRequired,
};

export const pageQuery = graphql`
  query SectionsPage($id: String!, $lang: String!) {
    language: markdownRemark(frontmatter: { code: { eq: $lang }, templateKey: { eq: "lang" } }) {
      frontmatter {
        metaTitle
        metaDescription
        metaKeywords
        privacyPolicyPopupContent
        privacyPolicyPopupButton
        homeMenuTitle
        footer {
          contactUs {
            title
            subtitle
            address
            googleMapsUrl
            email
            phones {
              number
            }
          }
          usefulLinks {
            title
            subtitle
            links {
              name
              url
            }
          }
          ourTeam {
            title
            subtitle
            members {
              name
              occupation
            }
          }
          footerImage1 {
            childImageSharp {
              fluid(maxWidth: 300) {
                ...GatsbyImageSharpFluid
              }
            }
            publicURL
          }
          footerImage2 {
            childImageSharp {
              fluid(maxWidth: 300) {
                ...GatsbyImageSharpFluid
              }
            }
            publicURL
          }
          copyright {
            by
            organization
          }
        }
      }
    }
    page: markdownRemark(id: { eq: $id }) {
      frontmatter {
        lang
        metaTitle
        metaDescription
        sections {
          type
          image {
            childImageSharp {
              fluid(maxWidth: 1080) {
                ...GatsbyImageSharpFluid
              }
            }
            publicURL
          }
          title
          subtitle
          content
          imageLeft {
            childImageSharp {
              fluid(maxWidth: 1080) {
                ...GatsbyImageSharpFluid
              }
            }
            publicURL
          }
          imageLeftColor
          imageRight {
            childImageSharp {
              fluid(maxWidth: 1080) {
                ...GatsbyImageSharpFluid
              }
            }
            publicURL
          }
          imageRightColor
          videoUrl
          videoFile {
            publicURL
          }
          maxCardsPerRow
          cards {
            type
            title
            url
            file {
              publicURL
            }
            external
            button
            icon {
              name
              type
              color
            }
            image {
              childImageSharp {
                fluid(maxWidth: 600) {
                  ...GatsbyImageSharpFluid
                }
              }
              publicURL
            }
            objectFit
            content
          }
        }
      }
    }
  }
`;
